import { useLayersBucket } from '../../../store/persistent/persistentStore';
import usePersistentStore from '@gui/store/persistent/persistentStore';
import { RichTreeView } from '@mui/x-tree-view/RichTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { TreeViewBaseItem } from '@mui/x-tree-view';
import { Globe01, Sun, LayersThree02, Eye, Target02, Wind02, Recording01, Bookmark, Dataflow01, X } from '@untitledui-pro/icons/line';
import { Box, Button, CircularProgress, Tooltip } from '@mui/material';
import { rename as renameGeodata } from '@api/geodataApi';
import { rename as renameSimulationSet } from '@api/simulationApi';
import { rename as renameLayer } from '@api/subsimulationApi';
import * as fileManager from '@api/fileManagerApi';
import useNotificationStore from '@gui/store/notification';
import type { Geodata } from '@data/Geodata';
import type { SimulationSet } from '@data/SimulationSet';
import { MIXED, NotificationType, SAVED, UUID } from '@visualizer/types';
import Layer from 'Simulations/Layer';
import LayerStandard from 'Simulations/LayerStandard';
import { getGrid } from '../../../../store/LoadingStore';
import SegmentsSection from './SegmentsSection';
import LayerSelectedRow from './LayerSelectedRow';
import { LayerInfoIndicator } from './LayerSelectedRow';
import { LayerVectorialScalarToggle } from './LayerSelectedRow';
import { getSentence } from '../../../constants/sentences';
import CustomTooltip from '@gui/components/generic/CustomTooltip';
import { useCapabilities } from '../../../../demo/capabilities';
import { useEnvStore, getDisabledInEnvMessage } from 'store/EnvStore';
import Grid from 'Geodata/grid';
import { convertFrame } from './dataUtils';
import { ScalarModeIcon, VectorialModeIcon } from './vectorialScalarModeIcons';

type LayerTreeProps = {
    onClick: (layerId: string) => void;
    isDataTreeview?: boolean;
    modeFilter?: 0 | 1;
    showModeIndicator?: boolean;
    hideMixedLayer?: boolean;
};

interface ExtendedTreeItem extends TreeViewBaseItem {
    iconKey?: string;
    nodeType?: 'geodata' | 'simset' | 'category' | 'layer' | 'failedLayer';
    showLabel?: boolean;
    failed?: boolean;
    failedReason?: string;
}

type LayerEntity = Layer & {
    getSimulationId?: () => number;
    simulationId?: number | string;
};

// Icon key mapping for categories
const getCategoryIcon = (category: string): string => {
    const iconKeyMap: Record<string, string> = {
        solar: 'sunny',
        freeview: 'visibility',
        targetview: 'modestandby',
        noise: 'graphiceq',
        wind: 'air',
        saved: 'save',
        mixed: 'tune',
        failed: 'failed',
    };
    return iconKeyMap[category] || 'layers'; // Default to 'layers' if category not found
};

const getItemLabel = (value: string | null | undefined, fallback: string): string => {
    const trimmedValue = value?.trim();
    return trimmedValue && trimmedValue.length > 0 ? trimmedValue : fallback;
};

const LayerTree: React.FC<LayerTreeProps> = ({
    onClick,
    isDataTreeview = false,
    modeFilter,
    showModeIndicator = false,
    hideMixedLayer = false,
}) => {
    const { canManageState, canRename } = useCapabilities();
    const env = useEnvStore((s) => s.env);
    const showNotification = useNotificationStore((state) => state.showNotification);
    const [editingItemId, setEditingItemId] = useState<string | null>(null);
    const [isRenaming, setIsRenaming] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [expandedItems, setExpandedItems] = useState<string[]>([]);
    const prevExpandableIdsRef = useRef<string[]>([]);
    const [saving, setSaving] = useState(false);

    // Icon map for rendering icons by key
    const iconMap: Record<string, React.ReactNode> = {
        language: <Globe01 className="icon-size-medium" />,
        layers: <LayersThree02 className="icon-size-medium" />,
        sunny: <Sun className="icon-size-medium" />,
        visibility: <Eye className="icon-size-medium" />,
        modestandby: <Target02 className="icon-size-medium" />,
        graphiceq: <Recording01 className="icon-size-medium" />,
        air: <Wind02 className="icon-size-medium" />,
        save: <Bookmark className="icon-size-medium" />,
        tune: <Dataflow01 className="icon-size-medium" style={{ transform: 'rotate(180deg)' }} />,
        failed: <X className="icon-size-medium" />,
    };

    const activeLayerId = useLayersBucket((s) => s.activeLayerID);
    const failedLayers = useLayersBucket((s) => s.failedLayers);
    const layerStates = useLayersBucket((s) => (showModeIndicator || modeFilter !== undefined ? s.layerStates : null));
    const grid = getGrid();
    const entityLookups = useMemo(() => {
        const geodataById = new Map<string, Geodata>();
        const simulationSetById = new Map<string, SimulationSet>();
        const layerById = new Map<string, LayerEntity>();

        if (!grid) {
            return { geodataById, simulationSetById, layerById };
        }

        for (const geodata of grid.geodatas) {
            geodataById.set(geodata.id, geodata);
            for (const simSet of geodata.simulationSets) {
                simulationSetById.set(simSet.id, simSet);
                for (const layer of simSet.layerCollection) {
                    layerById.set(String(layer.getUID()), layer as LayerEntity);
                }
            }
        }

        return { geodataById, simulationSetById, layerById };
    }, [grid, refreshKey]);

    const createItemsTree = useCallback((): ExtendedTreeItem[] => {
        if (!grid) return [];
        return grid.geodatas.map((geodata) => {
            const geodataId = geodata.id;

            return {
                id: geodataId,
                label: getItemLabel(geodata.name, geodataId),
                iconKey: 'language',
                nodeType: 'geodata' as const,
                children: geodata.simulationSets.map((simSet, simSetIndex) => {
                    const simSetId = simSet.id;

                    // Remove Mixed if hideMixerLayer is true, else return ALL layers:
                    const categoryEntries = Object.entries(simSet.layerCollectionByGroup)
                        .map(([category, layers]) => {
                            if (!hideMixedLayer) return [category, layers] as const;
                            if (category !== 'mixed') return [category, layers] as const;

                            // Remove only Mixed Layer, keep saved mixed layers:
                            return [category, layers.filter((layer) => String(layer.getUID()) !== 'mixed')] as const;
                        })
                        .filter(([_, layers]) => layers.length > 0);

                    const loadedLayerIds = new Set(categoryEntries.flatMap(([_, layers]) => layers.map((layer) => String(layer.getUID()))));
                    const failedFileEntries: ExtendedTreeItem[] = [];
                    const simNameBySubId = new Map(
                        (simSet.simulationSetJSON?.simulations ?? []).map((simulation) => [String(simulation.id), simulation.name])
                    );
                    const simSetLayerPrefix = `${simSet.id}-`;
                    for (const [layerUID, failedReason] of Object.entries(failedLayers)) {
                        if (!layerUID.startsWith(simSetLayerPrefix)) continue;
                        if (loadedLayerIds.has(layerUID)) continue;
                        const subSimulationId = layerUID.slice(simSetLayerPrefix.length);
                        failedFileEntries.push({
                            id: layerUID,
                            label: getItemLabel(simNameBySubId.get(subSimulationId), layerUID),
                            nodeType: 'failedLayer',
                            failed: true,
                            failedReason,
                        });
                    }

                    if (failedFileEntries.length > 0) {
                        categoryEntries.push(['failed', failedFileEntries as unknown as Layer[]]);
                    }

                    return {
                        id: simSetId,
                        label: getItemLabel(simSet.name, simSetId),
                        index: simSetIndex,
                        iconKey: 'layers',
                        nodeType: 'simset' as const,
                        children: categoryEntries.map(([category, layers]) => {
                            const categoryId = `${simSet.id}_${category}`;
                            const failedCategory = category === 'failed';

                            return {
                                id: categoryId,
                                label: getItemLabel(category, categoryId),
                                showLabel: false,
                                iconKey: getCategoryIcon(category),
                                nodeType: 'category' as const,
                                children: failedCategory
                                    ? (layers as unknown as ExtendedTreeItem[])
                                    : layers.map((layer) => ({
                                          id: `${layer.getUID()}`,
                                          label: getItemLabel(layer.name, String(layer.getUID())),
                                          nodeType: 'layer' as const,
                                          failed: !!failedLayers[String(layer.getUID())],
                                          failedReason: failedLayers[String(layer.getUID())],
                                      })),
                            };
                        }),
                    };
                }),
            };
        });
    }, [grid, failedLayers, hideMixedLayer]);

    const getExpandableIds = useCallback((item: ExtendedTreeItem): string[] => {
        const children = item.children ? (item.children as ExtendedTreeItem[]) : [];

        if (children.length === 0) {
            return [];
        }

        return [item.id, ...children.flatMap(getExpandableIds)];
    }, []);

    const items = useMemo(() => createItemsTree(), [createItemsTree, refreshKey]);

    const expandableItemIds = useMemo(() => items.flatMap(getExpandableIds), [items, getExpandableIds]);
    const expandableIdsSet = useMemo(() => new Set(expandableItemIds), [expandableItemIds]);

    useEffect(() => {
        setExpandedItems((current) => {
            const validIdsSet = new Set(expandableItemIds);
            const cleanedCurrent = current.filter((id) => validIdsSet.has(id));
            const previousIds = prevExpandableIdsRef.current;
            const previousIdSet = new Set(previousIds);
            const newIds = expandableItemIds.filter((id) => !previousIdSet.has(id));

            prevExpandableIdsRef.current = expandableItemIds;

            const shouldAutoExpandAll = cleanedCurrent.length === 0 && previousIds.length === 0;

            if (shouldAutoExpandAll) {
                return expandableItemIds;
            }

            const nextExpanded = new Set(cleanedCurrent);
            for (const id of newIds) {
                nextExpanded.add(id);
            }

            const nextArray = Array.from(nextExpanded);
            const hasChanged = nextArray.length !== current.length || nextArray.some((id) => !current.includes(id));

            return hasChanged ? nextArray : current;
        });
    }, [expandableItemIds]);

    const findItemById = useCallback((itemsList: ExtendedTreeItem[], id: string): ExtendedTreeItem | null => {
        for (const item of itemsList) {
            if (item.id === id) {
                return item;
            }
            if (item.children) {
                const found = findItemById(item.children as ExtendedTreeItem[], id);
                if (found) return found;
            }
        }
        return null;
    }, []);

    const cancelEditing = useCallback(() => {
        setEditingItemId(null);
    }, []);

    const handleRename = useCallback(
        async (item: ExtendedTreeItem | null, newLabel: string) => {
            if (!item || !item.nodeType || item.nodeType === 'category' || item.nodeType === 'failedLayer') return;

            const trimmedLabel = newLabel.trim();
            const currentLabel = typeof item.label === 'string' ? item.label : '';
            const trimmedCurrentLabel = currentLabel.trim();
            if (trimmedLabel === trimmedCurrentLabel) {
                cancelEditing();
                return;
            }

            if (!trimmedLabel) {
                const showNotification = useNotificationStore((state) => state.showNotification);
                showNotification('Name cannot be empty', NotificationType.Warning);
                return;
            }

            try {
                setIsRenaming(true);

                if (item.nodeType === 'geodata') {
                    const geo = entityLookups.geodataById.get(item.id);
                    if (!geo) {
                        throw new Error(`Geodata not found for item ${item.id}`);
                    }
                    await renameGeodata(trimmedLabel, { id: geo.id });
                    geo.name = trimmedLabel;
                } else if (item.nodeType === 'simset') {
                    const simulationSet = entityLookups.simulationSetById.get(item.id);
                    if (!simulationSet) {
                        throw new Error(`Simulation set not found for item ${item.id}`);
                    }
                    await renameSimulationSet(trimmedLabel, simulationSet);
                    simulationSet.name = trimmedLabel;
                } else if (item.nodeType === 'layer') {
                    const layerEntity = entityLookups.layerById.get(item.id);
                    if (!layerEntity) {
                        throw new Error(`Layer not found for item ${item.id}`);
                    }

                    const extractNumericId = (value: number | string | undefined | null): number | null => {
                        if (value === undefined || value === null) return null;
                        const numericValue = typeof value === 'string' ? Number(value) : value;
                        return Number.isNaN(numericValue) ? null : numericValue;
                    };

                    let subSimulationId = extractNumericId(
                        typeof layerEntity.getSimulationId === 'function' ? layerEntity.getSimulationId() : layerEntity.simulationId
                    );
                    let parentSimSetId: UUID | null = layerEntity.parentSimulationSet?.id ?? null;

                    if (subSimulationId === null && parentSimSetId && item.id.startsWith(`${parentSimSetId}-`)) {
                        subSimulationId = extractNumericId(item.id.slice(`${parentSimSetId}-`.length));
                    }

                    if (subSimulationId === null || !parentSimSetId) {
                        throw new Error('Layer data missing identifiers');
                    }

                    await renameLayer(trimmedLabel, {
                        id: subSimulationId,
                        parentSimulationSet: { id: parentSimSetId },
                    });
                    layerEntity.name = trimmedLabel;
                }

                setRefreshKey((key) => key + 1);
                cancelEditing();
            } catch (error) {
                console.error(error);
                showNotification('Error contacting server', NotificationType.Error);
            } finally {
                setIsRenaming(false);
            }
        },
        [cancelEditing, entityLookups, showNotification]
    );

    const saveLayer = useCallback(async () => {
        const currentGrid = getGrid();
        if (!currentGrid) return;

        const parentLayer = currentGrid.getActiveLayer();
        if (!parentLayer) return;

        if (!(parentLayer instanceof LayerStandard)) {
            showNotification('Layer not supported', NotificationType.Error);
            return;
        }

        const activeFrame = parentLayer.getActiveFrame();

        try {
            setSaving(true);
            const layerName = `${parentLayer.name}-${activeFrame.getName()}`;
            const values = convertFrame(activeFrame);
            const response = await fileManager.saveFrameInNewLayer(
                values,
                Grid.sensorPointsN,
                parentLayer.getSimKind(),
                layerName,
                activeFrame.getName(),
                parentLayer.getResult().resultLabel,
                parentLayer.getResult().unitOfMeasure
            );

            response.name = layerName;
            const savedLayer = await currentGrid.getSimulationSet().loadSavedLayer(response);
            usePersistentStore.getState().updateLayerState(savedLayer.getUID(), {
                resultDomainOverrides: {
                    vectorial: parentLayer.getResultDomain(0),
                    scalar: parentLayer.getResultDomain(1),
                },
            });
            setRefreshKey((key) => key + 1);
            showNotification('Layer saved', NotificationType.Success);
        } catch (error) {
            console.error(error);
            showNotification('Error saving frame in new layer', NotificationType.Error);
        } finally {
            setSaving(false);
        }
    }, [showNotification]);

    const isSaveButtonEnabled = useCallback((): boolean => {
        if (!canManageState) return false;
        if (saving) return false;
        const currentGrid = getGrid();
        if (!currentGrid) return false;
        const layer = currentGrid.getActiveLayer();
        if (!layer) return false;
        return !layer.isOfType(SAVED) && !layer.isOfType(MIXED);
    }, [saving, canManageState]);

    // Custom TreeItem component to handle icons
    const CustomTreeItem = ({ itemId, label, children, ...other }: any) => {
        const item = findItemById(items, itemId);
        const iconElem = item?.iconKey ? iconMap[item.iconKey] : undefined;
        const isLayerNode = item?.nodeType === 'layer';
        const layerLabelInset = isLayerNode ? 18 : 0;
        const isEditing = editingItemId === itemId;
        const isRenameable = canRename && isDataTreeview && item?.nodeType && ['geodata', 'simset', 'layer'].includes(item.nodeType);
        const hasChildren = Boolean(item && item.children && (item.children as ExtendedTreeItem[]).length > 0);
        const clickTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
        const inputRef = useRef<HTMLInputElement | null>(null);
        const selectionRef = useRef<{ start: number; end: number } | null>(null);
        const selectionRafRef = useRef<number | null>(null);
        const draftValueRef = useRef('');
        const CLICK_DELAY = 50;
        const [segmentsSectionIsOpened, setSegmentsSectionIsOpened] = useState<boolean>(false);
        const isActiveLayerNode = item?.nodeType === 'layer' && item?.id === activeLayerId;
        const isFailedNode = item?.failed || item?.nodeType === 'failedLayer';
        const layerMode = isLayerNode ? (layerStates?.[itemId]?.VECTORIAL_SCALAR_MODE ?? 0) : undefined;
        const isModeMismatch = isLayerNode && modeFilter !== undefined && layerMode !== modeFilter;
        const isDisabledLayer = isFailedNode || isModeMismatch;
        const isSegmentsSectionVisible = isDataTreeview && isActiveLayerNode && segmentsSectionIsOpened;

        const restoreSelection = useCallback(() => {
            const node = inputRef.current;
            if (!node) return;

            const valueLength = node.value.length;
            const selection = selectionRef.current;
            const start = Math.min(selection?.start ?? valueLength, valueLength);
            const end = Math.min(selection?.end ?? valueLength, valueLength);

            node.focus();
            node.setSelectionRange(start, end);
        }, []);

        const scheduleSelectionRestore = useCallback(() => {
            if (selectionRafRef.current !== null) {
                cancelAnimationFrame(selectionRafRef.current);
            }

            selectionRafRef.current = requestAnimationFrame(() => {
                selectionRafRef.current = null;
                restoreSelection();
            });
        }, [restoreSelection]);

        const clearClickTimeout = () => {
            if (clickTimeoutRef.current) {
                clearTimeout(clickTimeoutRef.current);
                clickTimeoutRef.current = null;
            }
        };

        useEffect(
            () => () => {
                clearClickTimeout();
            },
            []
        );

        const startEditing = () => {
            if (!item || !isRenameable) return;
            const initialText = typeof item.label === 'string' ? item.label : '';
            selectionRef.current = { start: initialText.length, end: initialText.length };
            draftValueRef.current = initialText;
            setEditingItemId(itemId);
        };

        useEffect(() => {
            if (isEditing) {
                scheduleSelectionRestore();
            } else {
                selectionRef.current = null;
            }

            return () => {
                if (selectionRafRef.current !== null) {
                    cancelAnimationFrame(selectionRafRef.current);
                    selectionRafRef.current = null;
                }
            };
        }, [isEditing, scheduleSelectionRestore]);

        return (
            <TreeItem
                className={`layer-tree-item${isActiveLayerNode ? ' layer-tree-item--active' : ''}`}
                onClick={(e) => {
                    if (!item) return;

                    e.stopPropagation();
                    clearClickTimeout();

                    const isIconClick = (e.target as HTMLElement).closest('.MuiTreeItem-iconContainer');
                    if (isIconClick) {
                        return;
                    }

                    if (isRenameable && e.detail === 2) {
                        const textTarget = (e.target as HTMLElement).closest('.layer-tree-item-label-text');
                        if (!textTarget) {
                            return;
                        }
                        clearClickTimeout();
                        startEditing();
                        return;
                    }

                    if (!hasChildren) {
                        if (isDisabledLayer) {
                            return;
                        }
                        clickTimeoutRef.current = setTimeout(() => {
                            clickTimeoutRef.current = null;
                            onClick(item.id);
                        }, CLICK_DELAY);
                        return;
                    }

                    clickTimeoutRef.current = setTimeout(() => {
                        clickTimeoutRef.current = null;

                        setExpandedItems((current) => {
                            const isExpanded = current.includes(itemId);
                            if (isExpanded) {
                                return current.filter((id) => id !== itemId);
                            }
                            return [...current, itemId];
                        });
                    }, CLICK_DELAY);
                }}
                itemId={itemId}
                label={
                    <div className={`layer-tree-item-label${isEditing ? ' layer-tree-item-label--editing' : ''}`}>
                        {iconElem}
                        {(item?.showLabel ?? true) &&
                            (isEditing ? (
                                <input
                                    ref={inputRef}
                                    className="layer-tree-input"
                                    defaultValue={typeof item?.label === 'string' ? item.label : ''}
                                    style={isLayerNode ? { paddingLeft: `${layerLabelInset}px` } : undefined}
                                    onClick={(event) => event.stopPropagation()}
                                    onChange={(event) => {
                                        const target = event.currentTarget;
                                        draftValueRef.current = target.value;
                                        selectionRef.current = {
                                            start: target.selectionStart ?? target.value.length,
                                            end: target.selectionEnd ?? target.value.length,
                                        };
                                        scheduleSelectionRestore();
                                    }}
                                    onKeyDown={(event) => {
                                        event.stopPropagation();

                                        if (event.key === 'Enter') {
                                            event.preventDefault();
                                            const nextValue = inputRef.current ? inputRef.current.value : draftValueRef.current;
                                            void handleRename(item, nextValue);
                                        } else if (event.key === 'Escape') {
                                            event.preventDefault();
                                            cancelEditing();
                                        }
                                    }}
                                    onSelect={(event) => {
                                        const target = event.currentTarget;
                                        selectionRef.current = {
                                            start: target.selectionStart ?? target.value.length,
                                            end: target.selectionEnd ?? target.value.length,
                                        };
                                        scheduleSelectionRestore();
                                    }}
                                    onBlur={() => {
                                        if (isRenaming) return;
                                        const nextValue = inputRef.current ? inputRef.current.value : draftValueRef.current;
                                        void handleRename(item, nextValue);
                                    }}
                                />
                            ) : (
                                <Box>
                                    <Box display="flex" alignItems="center" sx={{ gap: 0.5, maxWidth: '100%', position: 'relative' }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                flex: 1,
                                                minWidth: 0,
                                                position: 'relative',
                                            }}
                                        >
                                            {isDataTreeview && isActiveLayerNode && <LayerVectorialScalarToggle layerId={item.id} />}
                                            {showModeIndicator && isLayerNode && (
                                                <Box className={`layer-mode-indicator${isModeMismatch ? ' is-disabled' : ''}`}>
                                                    {layerMode === 0 ? (
                                                        <VectorialModeIcon className="layer-mode-toggle__icon" />
                                                    ) : (
                                                        <ScalarModeIcon className="layer-mode-toggle__icon" />
                                                    )}
                                                </Box>
                                            )}
                                            {isFailedNode ? (
                                                <CustomTooltip
                                                    mode="hover"
                                                    title={<Box>{item?.failedReason || 'Layer loading failed'}</Box>}
                                                >
                                                    <Box
                                                        className="layer-tree-item-label-text"
                                                        sx={{
                                                            flex: 1,
                                                            minWidth: 0,
                                                            maxWidth: '100%',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            pl: `${layerLabelInset}px`,
                                                            color: 'error.main',
                                                            fontWeight: 600,
                                                            cursor: 'not-allowed',
                                                        }}
                                                    >
                                                        {label}
                                                    </Box>
                                                </CustomTooltip>
                                            ) : (
                                                <Box
                                                    className="layer-tree-item-label-text"
                                                    sx={{
                                                        flex: 1,
                                                        minWidth: 0,
                                                        maxWidth: '100%',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        pl: `${layerLabelInset}px`,
                                                        opacity: isModeMismatch ? 0.45 : 1,
                                                        cursor: isModeMismatch ? 'not-allowed' : 'inherit',
                                                    }}
                                                >
                                                    {label}
                                                </Box>
                                            )}
                                            {isDataTreeview && isActiveLayerNode && <LayerInfoIndicator />}
                                        </Box>
                                        {isDataTreeview && isActiveLayerNode && (
                                            <LayerSelectedRow
                                                isOpen={segmentsSectionIsOpened}
                                                onToggle={() => setSegmentsSectionIsOpened((open) => !open)}
                                            />
                                        )}
                                    </Box>
                                    {isSegmentsSectionVisible && <SegmentsSection />}
                                </Box>
                            ))}
                    </div>
                }
                {...other}
            >
                {children}
            </TreeItem>
        );
    };

    return (
        <Box sx={{ pr: 0 }}>
            <RichTreeView
                className="data-tree-view"
                items={items}
                expandedItems={expandedItems}
                disableSelection
                expansionTrigger="iconContainer"
                onExpandedItemsChange={(_, newExpanded) => {
                    const nextExpanded = newExpanded.filter((id) => expandableIdsSet.has(id));
                    setExpandedItems(nextExpanded);
                }}
                slots={{
                    item: CustomTreeItem,
                }}
            />

            {isDataTreeview && (
                <Box sx={{ display: 'flex', justifyContent: 'end' }}>
                    <Tooltip title={!canManageState ? getDisabledInEnvMessage(env) : getSentence('Data.Treeview.Save.Tooltip')}>
                        <span>
                            <Button variant="contained" disabled={!isSaveButtonEnabled()} onClick={() => void saveLayer()} sx={{ mt: 1.5 }}>
                                Save
                                {saving && <CircularProgress size={16} color="inherit" sx={{ ml: 1 }} />}
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
            )}
        </Box>
    );
};

export default LayerTree;
